import { jwtDecode } from "jwt-decode";
import type { DecodedToken } from "@/interfaces";

/**
 * Token source types for flexible token retrieval
 */
export type TokenSource = 'url' | 'storage' | 'any';

/**
 * Gets JWT token from URL parameters (ignores PayPal tokens)
 */
export function getTokenFromUrl(): string | null {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    // If token exists, check if it's a JWT token (starts with 'eyJ')
    // PayPal tokens are short alphanumeric strings, JWT tokens start with 'eyJ'
    if (token && token.startsWith('eyJ')) {
      return token;
    }
    
    return null;
  } catch (error) {
    console.error('Failed to get token from URL:', error);
    return null;
  }
}

/**
 * Gets JWT token from sessionStorage with backup fallback
 */
export function getTokenFromStorage(): string | null {
  try {
    // Try to get the primary token
    let token = sessionStorage.getItem("token");
    
    // If token exists but is not a JWT (e.g., PayPal token), try backup
    if (token && !token.startsWith('eyJ')) {
      const backupToken = sessionStorage.getItem("original_jwt_token");
      if (backupToken && backupToken.startsWith('eyJ')) {
        sessionStorage.setItem("token", backupToken);
        token = backupToken;
      }
    }
    
    // If no token found, try backup
    if (!token) {
      const backupToken = sessionStorage.getItem("original_jwt_token");
      if (backupToken && backupToken.startsWith('eyJ')) {
        sessionStorage.setItem("token", backupToken);
        token = backupToken;
      }
    }
    
    return token;
  } catch (error) {
    console.error('Failed to get token from storage:', error);
    return null;
  }
}

/**
 * Gets JWT token from specified source with fallback logic
 */
export function getToken(source: TokenSource = 'any'): string | null {
  switch (source) {
    case 'url':
      return getTokenFromUrl();
    case 'storage':
      return getTokenFromStorage();
    case 'any':
      // Try URL first, then storage
      return getTokenFromUrl() || getTokenFromStorage();
    default:
      return null;
  }
}

/**
 * Decodes a JWT token string
 */
export function getDecodedToken(token: string): DecodedToken | null {
  try {
    // Additional safety check: ensure token has 3 parts (JWT format)
    if (!token || token.split('.').length !== 3) {
      sessionStorage.removeItem('token');
      return null;
    }
    return jwtDecode<DecodedToken>(token);
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
}

/**
 * Gets decoded JWT token from specified source
 */
export function getDecodedTokenFromSource(source: TokenSource = 'any'): DecodedToken | null {
  const token = getToken(source);
  if (!token) return null;
  return getDecodedToken(token);
}

/**
 * Extracts appId from JWT token with flexible source
 */
export function getAppId(source: TokenSource = 'any'): string | null {
  const decoded = getDecodedTokenFromSource(source);
  return decoded?.appId || null;
}

/**
 * Extracts returnDeeplink from JWT token with flexible source
 */
export function getReturnDeeplink(source: TokenSource = 'any'): string | null {
  const decoded = getDecodedTokenFromSource(source);
  return decoded?.returnDeeplink || null;
}

/**
 * Extracts expiration timestamp from JWT token with flexible source
 */
export function getExpiration(source: TokenSource = 'any'): number | null {
  const decoded = getDecodedTokenFromSource(source);
  return decoded?.exp || null;
}

/**
 * Checks if a JWT token is expired
 */
export function isTokenExpired(source: TokenSource = 'any'): boolean {
  const exp = getExpiration(source);
  if (!exp) return true;
  
  const now = Math.floor(Date.now() / 1000);
  return now >= exp;
}

/**
 * Stores JWT token in sessionStorage
 */
export function storeToken(token: string): void {
  try {
    sessionStorage.setItem("token", token);
  } catch (error) {
    console.error('Failed to store token:', error);
  }
}

/**
 * Clears JWT token from sessionStorage
 */
export function clearStoredToken(): void {
  try {
    sessionStorage.removeItem("token");
  } catch (error) {
    console.error('Failed to clear stored token:', error);
  }
}

// Legacy compatibility functions - will be deprecated
/**
 * @deprecated Use getDecodedTokenFromSource('url') instead
 */
export function getDecodedTokenFromUrl(): DecodedToken | null {
  return getDecodedTokenFromSource('url');
}

/**
 * @deprecated Use getReturnDeeplink('url') instead
 */
export function getReturnDeeplinkFromUrl(): string | null {
  return getReturnDeeplink('url');
}

/**
 * @deprecated Use getAppId('url') instead
 */
export function getAppIdFromUrl(): string | null {
  return getAppId('url');
}

/**
 * @deprecated Use getAppId('storage') instead
 */
export function getGameIdFromToken(): string | null {
  return getAppId('storage');
}

/**
 * Stores appId in localStorage as fallback
 */
export function storeAppIdFallback(appId: string): void {
  try {
    localStorage.setItem('lastKnownAppId', appId);
  } catch (error) {
    console.error('Failed to store appId fallback:', error);
  }
}

/**
 * Gets stored appId from localStorage
 */
export function getStoredAppIdFallback(): string | null {
  try {
    return localStorage.getItem('lastKnownAppId');
  } catch (error) {
    console.error('Failed to get stored appId fallback:', error);
    return null;
  }
}

/**
 * Gets PayPal JWT token for a specific session ID
 */
export function getPayPalJwtToken(sessionId?: string): string | null {
  try {
    // Try session-specific token first
    if (sessionId) {
      const sessionToken = sessionStorage.getItem(`paypal_jwt_${sessionId}`) ||
                          localStorage.getItem(`paypal_jwt_${sessionId}`);
      if (sessionToken) {
        return sessionToken;
      }
    }
    
    // Fallback to generic PayPal token
    return sessionStorage.getItem('paypal_jwt_token');
  } catch (error) {
    console.error('Failed to get PayPal JWT token:', error);
    return null;
  }
}

/**
 * Stores PayPal JWT token for a specific session ID
 */
export function storePayPalJwtToken(sessionId: string, token: string): void {
  try {
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('paypal_jwt_token', token);
    sessionStorage.setItem(`paypal_jwt_${sessionId}`, token);
    localStorage.setItem(`paypal_jwt_${sessionId}`, token);
  } catch (error) {
    console.error('Failed to store PayPal JWT token:', error);
  }
}
